/**
 * One breadcrumb trail per page, rendered twice from the same data.
 *
 * Solution, industry, blog and head-to-head pages used to write their visible
 * breadcrumb in the template and their `BreadcrumbList` in the registry, and the
 * two drifted: the markup named a page one thing while the trail on screen named
 * it another. Google expects breadcrumb markup to describe the trail a visitor
 * can see, so each registry now exposes a `get…Breadcrumbs()` function that
 * returns the trail once. `components/marketing/Breadcrumbs.tsx` renders it as
 * crawlable links back up the hierarchy, and `breadcrumbListSchema()` turns the
 * same list into the node the registry's schema builder hands to
 * `composeRouteGraph()` — so the JSON-LD still ships through the route's single
 * `@graph`, never as a script of its own.
 *
 * Deliberately free of imports from `siteSeo.ts`: the registries that call this
 * are imported by `siteSeo.ts`, and the origin is restated here for the same
 * reason each registry restates it.
 */
import { SCHEMA_CONTEXT, type JsonLd } from './structuredData';

const SITE_ORIGIN = 'https://www.clear-talent.com';

export interface BreadcrumbItem {
  /** The label shown on the page and used as the ListItem `name`. */
  name: string;
  /** Site-relative path. The last item is the current page. */
  path: string;
}

/** The trail as a schema.org `BreadcrumbList` node with absolute item URLs. */
export const breadcrumbListSchema = (items: BreadcrumbItem[]): JsonLd => ({
  '@context': SCHEMA_CONTEXT,
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_ORIGIN}${item.path}`,
  })),
});
